import Container from '@components/atoms/container/searchVirtualKeyboardContainer';
import VirtualKeyboard from './virtualKeyboard';
import Focus from '@shared/types/enums/focus';
import { AutoComplete } from '@shared/types/interfaces/search';
import DeleteKey from '@components/elements/keys/deleteKey';
import VIRTUAL_KEYBOARD_ACTIONS from '@shared/types/enums/virtualKeyboardActions';
import Key, { JumpPosition } from '@shared/classes/key';
import SpaceKey from '@components/elements/keys/spaceKey';
import EraseKey from '@components/elements/keys/eraseKey';
import ConfirmKey from '@components/elements/keys/confirmKey';
import { useMemo } from 'react';
import { numbers } from './keyData';
import useVirtualKeyboard from '../../../utils/hooks/useVirtualKeyboard';
import LettersKey from '@components/elements/keys/lettersKey';
import { KEYBOARDS } from '../search/searchKeyboard';

const MAX_COLS = 6;

function NumbersKeyboard({
  clearAction,
  searchAction,
  onChange,
  onDelete,
  outOfBounds,
  swapKeyboardAction,
}: {
  clearAction: () => void;
  searchAction: (query?: AutoComplete) => void;
  onChange: (char: string) => void;
  onDelete: (pressed?: boolean) => void;
  outOfBounds: (focus: Focus, key: string) => void;
  swapKeyboardAction: React.Dispatch<React.SetStateAction<KEYBOARDS>>;
}) {
  const { getKeys, getGaps } = useVirtualKeyboard(MAX_COLS);

  const keys = useMemo(() => {
    // Create squared gaps and keys
    const gaps = getGaps([numbers], false);
    const { keys: numbersKeys, stride } = getKeys(numbers, gaps, 0, false);
    const charKeys = [...numbersKeys, ...gaps].sort(
      (a, b) => (a.spaces as number) - (b.spaces as number)
    );

    // Create irregular-sized keys

    const spaceIndex = [stride, stride + 1];
    const spaceKey = new Key({
      action: VIRTUAL_KEYBOARD_ACTIONS.WRITE,
      component: (
        <SpaceKey
          char={' '}
          index={spaceIndex}
          key={spaceIndex[0]}
          alternateKeyboard={true}
        />
      ),
      jump: new JumpPosition(
        spaceIndex[0] - 1,
        -1,
        spaceIndex[0] - MAX_COLS,
        spaceIndex[0] + MAX_COLS
      ),
      spaces: spaceIndex,
    });

    const deleteIndex = [stride + 2];

    const deleteKey = new Key({
      action: VIRTUAL_KEYBOARD_ACTIONS.DELETE,
      component: (
        <DeleteKey
          index={deleteIndex}
          key={deleteIndex[0]}
          alternateKeyboard={true}
        />
      ),
      jump: new JumpPosition(
        -1,
        deleteIndex[0] + 1,
        deleteIndex[0] - MAX_COLS,
        -1
      ),
      spaces: deleteIndex,
    });

    const lettersIndex = [stride + 3, stride + 4];
    const lettersKey = new Key({
      action: VIRTUAL_KEYBOARD_ACTIONS.CUSTOM,
      component: <LettersKey index={lettersIndex} key={lettersIndex[0]} />,
      jump: new JumpPosition(
        lettersIndex[0] - 1,
        lettersIndex[1] + 1,
        lettersIndex[0] - MAX_COLS,
        -1
      ),
      spaces: lettersIndex,
      customAction: () => swapKeyboardAction(KEYBOARDS.Letters),
    });

    const clearIndex = [stride + 5];
    const clearKey = new Key({
      action: VIRTUAL_KEYBOARD_ACTIONS.CUSTOM,
      component: (
        <EraseKey
          index={clearIndex}
          key={clearIndex[0]}
          alternateKeyboard={true}
        />
      ),
      jump: new JumpPosition(
        clearIndex[0] - 1,
        clearIndex[0] + 1,
        clearIndex[0] - MAX_COLS,
        -1
      ),
      spaces: clearIndex,
      customAction: clearAction,
    });

    const confirmIndex = [stride + 6, stride + 7];
    const confirmKey = new Key({
      action: VIRTUAL_KEYBOARD_ACTIONS.CUSTOM,
      component: (
        <ConfirmKey
          index={confirmIndex}
          key={confirmIndex[0]}
          alternateKeyboard={true}
        />
      ),
      jump: new JumpPosition(
        confirmIndex[0] - 1,
        -1,
        confirmIndex[0] - MAX_COLS,
        -1
      ),
      spaces: confirmIndex,
      customAction: searchAction,
    });
    // Merge and sort gaps and keys
    return [...charKeys, spaceKey, deleteKey, lettersKey, clearKey, confirmKey];
  }, []);

  return (
    <Container>
      <VirtualKeyboard
        focus={Focus.SEARCH_KEYBOARD}
        keys={keys}
        maxCols={MAX_COLS}
        onChange={onChange}
        onDelete={onDelete}
        outOfBounds={outOfBounds}
      />
    </Container>
  );
}

export default NumbersKeyboard;
